import React, { Suspense, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import {
  Header as HeaderBase,
  useViewportSize,
  useFeature,
} from '@ole-ui/ole-ui-components';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { Link } from 'react-mobx-router5';
import Avatar from 'react-avatar';
import { storesContext } from 'stores';
import type { IRootStore } from 'stores/IRootStore';
import { logout } from 'services/AuthService';
import MenuIcon from 'img/icons/menu.svg';
import SearchIcon from 'img/icons/search-h.svg';
import LanguageIcon from 'img/icons/language-h.svg';
import SitesIcon from 'img/icons/sites-h.svg';
import RHLSLogo from 'img/rhls_logo.svg';
import RHTCLogo from 'img/rh-training-certification-logo.svg';
import { RHU_SUPPORT_URL, SKILLS_ASSESSMENT_URL } from 'config/constants';
import { isLocalhost } from 'helpers/utils';
import { FeatureFlagManagerProvider } from 'components/FeatureFlagManager';
import SearchAutosuggest from 'components/Search';
import { Badge } from '@patternfly/react-core';
import LaneIds from 'views/MyTraining/Lanes/types';
import SubscriptionWidget from '../Subscriptions/LineWidget';
import TopBar from './TopBar';
import LanguageMenu from './LanguageMenu';
import AccountMenu from './AccountMenu';
import SitesMenu from './SitesMenu';

import './styles.scss';

const FeatureFlagManagerModal = React.lazy(
  () => import('components/FeatureFlagManager/Modal'),
);

const PrefetchSearchV2 = () => {
  useEffect(() => {
    import(/* webpackChunkName: 'search-v2' */ 'views/Search/SearchV2').catch(
      () => {},
    );
  }, []);

  return null;
};

/** Nested link in a dropdown (matches @ole-ui Header API) */
interface NavigationLink {
  key: string;
  text: string;
  onClick: () => void;
  active?: boolean;
  'data-analytics-id'?: string;
  renderIf?: boolean;
}

/** Navigation item: link or dropdown (matches @ole-ui Header API) */
interface NavigationItem {
  key: string;
  text: string;
  onClick?: () => void;
  active?: boolean;
  'data-analytics-id'?: string;
  renderIf?: boolean;
  href?: string;
  items?: NavigationLink[];
}

/** Menu item for Header side menu (matches @ole-ui Header API) */
interface MenuItem {
  key: string;
  text: string;
  icon: React.ReactNode;
  content:
    | 'navigation'
    | React.ReactNode
    | ((params: {
        setMenuOpen: (open: boolean) => void;
        isMenuOpen: boolean;
      }) => React.ReactNode);
  'data-analytics-id'?: string;
  renderIf?: boolean;
  positionOnMobile?: 'bottom' | 'top';
  indexOnMobile?: number;
}

const Header = (): React.ReactElement => {
  const { i18n, t } = useTranslation();
  const { isLowerThanMd: isMobile, isLowerThanSm: isHomeMobile } =
    useViewportSize();
  const {
    routerStore,
    uiStore,
    userStore,
    eventStore,
    configStore,
    featuresStore,
    userNotificationsStore,
    classesStore,
  } = useContext(storesContext) as IRootStore;
  const { languages } = uiStore;
  const language = languages.find((lang) => lang.token === i18n.language);
  const languageName = language?.display_name;
  const { route } = routerStore;
  const { isLoggedIn, avatar, user } = userStore;
  const { preferred_name: preferredName } = user;

  const showConnectivityTester = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ConnectivityTesterEnabled,
    defaultFlagValue: false,
  });

  const isLiveClassEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.LiveClassEnabled,
    defaultFlagValue: false,
  });

  const canViewUserDashboard =
    useFeature({
      section: 'enablements',
      flagName: EnablementFeatureFlags.UserDashboardEnabled,
      defaultFlagValue: false,
    }) &&
    userStore.isLoggedIn &&
    !userStore.isVTonly;

  const isNewMyTrainingNavigationEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewMyTrainingNavigation,
    defaultFlagValue: false,
  });

  const canViewCatalog =
    useFeature({
      section: 'enablements',
      flagName: EnablementFeatureFlags.CanViewCatalog,
      defaultFlagValue: false,
    }) &&
    userStore.isLoggedIn &&
    classesStore.hasCurrentEnrollment;

  const canViewReports =
    useFeature({
      section: 'enablements',
      flagName: EnablementFeatureFlags.CanViewReports,
      defaultFlagValue: false,
    }) && userStore.isLoggedIn;

  const savedForLaterCardsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SavedForLaterCardsEnabled,
    defaultFlagValue: false,
  });

  const onLogout = async (): Promise<void> => {
    await logout();
    uiStore.clearLabAutostartSettings();
    if (window.Intercom) {
      window.Intercom('shutdown');
    }
  };

  const handleMobileMenuChange = (isOpen: boolean): void => {
    window?.Intercom?.('update', {
      hide_default_launcher: isOpen,
    });
  };

  const subscriptionDataReady =
    userStore.didFetchSubscription && classesStore.didFetchClassesForUser;

  const canViewLogo = subscriptionDataReady
    ? !userStore.isOpenSubscriber
    : true;
  const hasManagerRights =
    (userStore.isLoggedIn && userStore.isOrganizationManager) ||
    userStore.isOrganizationAdmin;

  const isNewSearchEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewSearch,
    defaultFlagValue: false,
  });

  const isSkillsPathsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    defaultFlagValue: false,
  });
  const canViewSkillsPaths =
    userStore.isLoggedIn &&
    featuresStore.allFeaturesFetched &&
    (isSkillsPathsEnabled || userStore.isOrganizationLeader);

  const canViewHelp =
    (userStore.isLoggedIn && !userStore.isExternalSubscriber) ||
    (userStore.isExternalSubscriber && userStore.isOpenSubscriber);
  const routeParams = (route as { params?: Record<string, string> }).params;
  const canViewSubscriptionWidget =
    userStore.isLoggedIn &&
    (userStore.isExternalSubscriber ||
      userStore.isAlacarteSubscriberForCourse(routeParams?.course)) &&
    route.name.includes('courses');

  const canViewConnectivityTester = showConnectivityTester;

  const navigationItems: NavigationItem[] = [
    {
      renderIf: userStore.isLoggedIn && !isHomeMobile,
      key: 'home',
      onClick: () => {
        routerStore.navigate('home');
      },
      active: route.name === 'home',
      'data-analytics-id': 'home-nav-link-header-lp',
      text: t('Home'),
    },
    {
      renderIf: userStore.isLoggedIn && isHomeMobile,
      key: 'my-activity',
      onClick: () => {
        routerStore.navigate('home', {
          tab: 'my-activity',
        });
      },
      'data-analytics-id': 'my-activity-nav-link-header-lp',
      text: isHomeMobile ? t('My activity') : t('My Activity DT'),
    },
    {
      renderIf: userStore.isLoggedIn && isHomeMobile,
      key: 'my-training',
      'data-analytics-id': 'my-training-nav-link-header-lp',
      text: isHomeMobile ? t('My Training') : t('My Training DT'),
      ...(isNewMyTrainingNavigationEnabled
        ? {
            items: [
              {
                key: 'my-training:live-events',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.LiveEvents,
                  });
                },
                'data-analytics-id':
                  'live-events-my-training-nav-link-header-lp',
                text: t('Live Events'),
              },
              {
                key: 'my-training:continue-learning',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.ContinueLearning,
                  });
                },
                'data-analytics-id':
                  'continue-learning-my-training-nav-link-header-lp',
                text: t('Continue learning'),
              },
              {
                key: 'my-training:trial-subscriptions',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.TrialSubscriptions,
                  });
                },
                'data-analytics-id':
                  'trial-subscriptions-my-training-nav-link-header-lp',
                text: t('Trial subscriptions'),
              },
              {
                key: 'my-training:my-skills-paths',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.SkillsPaths,
                  });
                },
                'data-analytics-id':
                  'my-skills-paths-my-training-nav-link-header-lp',
                text: t('Enrolled skills paths'),
              },
              {
                renderIf: savedForLaterCardsEnabled,
                key: 'my-training:favorites',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.SavedForLater,
                  });
                },
                'data-analytics-id': 'favorites-my-training-nav-link-header-lp',
                text: t('Saved for later'),
              },
              {
                key: 'my-training:new-offerings',
                onClick: () => {
                  routerStore.navigate('home', {
                    tab: 'my-training',
                    selectedLane: LaneIds.NewOfferings,
                  });
                },
                'data-analytics-id':
                  'new-offerings-my-training-nav-link-header-lp',
                text: t('New'),
              },
            ],
          }
        : {
            onClick: () => {
              routerStore.navigate('home', {
                tab: 'my-training',
              });
            },
          }),
    },
    {
      renderIf: hasManagerRights,
      key: 'dashboard-manager',
      'data-analytics-id': 'dashboard-nav-link-header-lp',
      text: t('Dashboard'),
      active: route.name.includes('dashboard:'),
      items: [
        ...(canViewUserDashboard
          ? [
              {
                key: 'dashboard:user',
                onClick: () => {
                  routerStore.navigate('dashboard:user');
                },
                'data-analytics-id': 'my-progress-dashboard-nav-link-header-lp',
                text: t('My Progress'),
              },
            ]
          : []),
        {
          key: 'dashboard:overview',
          onClick: () => {
            routerStore.navigate('dashboard:overview');
          },
          'data-analytics-id': 'mgmnt-overview-dashboard-nav-link-header-lp',
          text: t('Management Overview'),
        },
      ],
    },
    {
      renderIf: canViewUserDashboard && !hasManagerRights,
      key: 'dashboard',
      onClick: () => {
        routerStore.navigate('dashboard:user');
      },
      active: route.name === 'dashboard:user',
      'data-analytics-id': 'my-progress-dashboard-nav-link-header-lp',
      text: t('Dashboard'),
    },
    {
      renderIf: canViewCatalog,
      key: 'catalog',
      onClick: () => {
        routerStore.navigate('catalog');
      },
      active: route.name === 'catalog',
      'data-analytics-id': 'catalog-nav-link-header-lp',
      text: t('Catalog'),
    },
    {
      renderIf: userStore.isTrainingPartner,
      key: 'subscriptions',
      onClick: () => {
        routerStore.navigate('partner:subscriptions');
      },
      active: route.name === 'partner:subscriptions',
      'data-analytics-id': 'subscriptions-nav-link-header-lp',
      text: t('Subscriptions'),
    },
    // Premium sessions catalog (premium:sessions:live) and calendar (premium:sessions:calendar)
    // remain available via routes; direct header access removed for now.
    {
      renderIf: isLiveClassEnabled,
      key: 'premium-sessions',
      'data-analytics-id': 'live-classes-nav-link-header-lp',
      text: t('Live classes'),
      onClick: () => {
        // reload: true so same-route param change (e.g. already on catalog) triggers transition
        routerStore.navigate(
          'catalog',
          { deliveryFormats: 'course' },
          // @ts-expect-error mobx-router5 navigate accepts options as 3rd arg; types omit it
          { reload: true },
        );
      },
    },
    {
      renderIf: canViewSkillsPaths,
      key: 'skills-paths',
      onClick: () => {
        routerStore.navigate('skills-paths');
      },
      active: route.name === 'skills-paths',
      'data-analytics-id': 'kills-paths-nav-link-header-lp',
      text: t('Skills Paths'),
    },
    {
      renderIf: canViewReports,
      key: 'reports',
      onClick: () => {
        routerStore.navigate('reports');
      },
      active: route.name === 'reports',
      'data-analytics-id': 'reports-nav-link-header-lp',
      text: t('Reports'),
    },
    {
      renderIf: isHomeMobile && !!configStore.ebooksPlatformURL,
      key: 'ebooks',
      href: configStore.ebooksPlatformURL,
      'data-analytics-id': 'ebooks-nav-link-header-lp',
      text: t('Access bookshelf'),
    },
    {
      renderIf: isHomeMobile,
      key: 'skills-assessment',
      href: SKILLS_ASSESSMENT_URL,
      'data-analytics-id': 'skills-assessment-nav-link-header-lp',
      text: t('Skills assessment'),
    },
    {
      renderIf: canViewHelp,
      key: 'help-dropdown',
      'data-analytics-id': 'help-nav-link-header-lp',
      text: t('Help'),
      active: route.name.includes('faq'),
      items: [
        ...(userStore.isLoggedIn && !userStore.isRHUSubscriber
          ? [
              {
                key: 'support-form',
                onClick: () => {
                  eventStore.toggleDrawer('support_form');
                },
                'data-analytics-id': 'support-header-lp',
                text: t('Support'),
              },
            ]
          : [
              {
                key: 'support-external',
                onClick: () => {
                  window.open(
                    configStore?.config?.OLE_SUPPORT_URL || RHU_SUPPORT_URL,
                    '_blank',
                  );
                },
                'data-analytics-id': 'support-header-lp',
                text: t('Support'),
              },
            ]),
        {
          key: 'faq',
          onClick: () => {
            routerStore.navigate('faq');
          },
          'data-analytics-id': 'faq-header-lp',
          text: t('FAQ'),
        },
        {
          renderIf: canViewConnectivityTester,
          key: 'connectivity',
          onClick: () => {
            routerStore.navigate('connectivity');
          },
          'data-analytics-id': 'connectivity-header-lp',
          text: t('Connectivity Tester'),
        },
      ],
    },
  ];

  const notificationCount =
    eventStore.newNotifications.length +
      userNotificationsStore?.newUserNotifications?.length || 0;

  const menuItems: MenuItem[] = [
    {
      key: 'menu',
      'data-analytics-id': 'mobile-menu-header-lp',
      icon: <SVG src={MenuIcon}>{t('Menu')}</SVG>,
      text: t('Menu'),
      content: 'navigation',
      renderIf: userStore.isLoggedIn,
    },
    {
      renderIf: true,
      key: 'languages',
      'data-analytics-id': 'language-switcher-header-lp',
      icon: <SVG src={LanguageIcon}>{t('Languages')}</SVG>,
      text: languageName || t('Language'),
      positionOnMobile: 'bottom',
      indexOnMobile: 1,
      content: ({ setMenuOpen }) => <LanguageMenu setMenuOpen={setMenuOpen} />,
    },
    {
      renderIf: userStore.isLoggedIn && classesStore.hasCurrentEnrollment,
      key: 'search',
      'data-analytics-id': 'search-menu-header-lp',
      icon: <SVG src={SearchIcon}>{t('Search')}</SVG>,
      text: t('Search'),
      positionOnMobile: 'top',
      content: ({ setMenuOpen }) => (
        <div className="header__menu-item">
          {isNewSearchEnabled && <PrefetchSearchV2 />}
          <SearchAutosuggest setMenuOpen={setMenuOpen} autoFocus={!isMobile} />
        </div>
      ),
    },
    {
      renderIf: userStore.isLoggedIn,
      key: 'account',
      'data-analytics-id': 'account-menu-header-lp',
      icon: isLoggedIn ? (
        <div className="notification-badge-wrapper">
          <Avatar
            className="avatar"
            size="30"
            round
            name={preferredName}
            src={avatar}
          />
          {!!notificationCount && (
            <Badge className="notification-badge absolute">
              {notificationCount}
            </Badge>
          )}
        </div>
      ) : (
        <SVG src={SitesIcon}>{t('Red Hat Sites')}</SVG>
      ),
      text: isLoggedIn ? preferredName || t('Account') : t('Account'),
      positionOnMobile: 'bottom',
      indexOnMobile: 0,
      content: ({ setMenuOpen }) => (
        <div className="header__menu-item">
          <AccountMenu setMenuOpen={setMenuOpen} onLogout={onLogout} />
        </div>
      ),
    },
    {
      renderIf: true,
      'data-analytics-id': 'sites-menu-header-lp',
      key: 'sites',
      text: t('Sites'),
      positionOnMobile: 'bottom',
      icon: <SVG src={SitesIcon}>{t('Red Hat Sites')}</SVG>,
      content: (
        <div
          className="header__menu-item"
          data-analytics-id="sites-viewbox-header-lp"
        >
          <SitesMenu />
        </div>
      ),
      indexOnMobile: 2,
    },
  ];

  const showFeatureFlagManager = isLocalhost || userStore.isSuperuser;

  return (
    <FeatureFlagManagerProvider enabled={showFeatureFlagManager}>
      <div>
        <TopBar />
        <HeaderBase
          className="header"
          menuItems={featuresStore.allFeaturesFetched ? menuItems : []}
          navigationItems={navigationItems}
          onMobileMenuChange={handleMobileMenuChange}
        >
          {canViewLogo && !userStore.isWD200Subscriber && (
            <HeaderBase.Logo>
              <Link
                href
                routeName="home"
                data-analytics-id="rhls-logo-header-lp"
              >
                <img
                  src={RHLSLogo}
                  className="logo"
                  alt={t('Red Hat Learning Subscription logo')}
                />
              </Link>
            </HeaderBase.Logo>
          )}
          {canViewLogo && userStore.isWD200Subscriber && (
            <HeaderBase.Logo>
              <Link href routeName="home">
                <img
                  src={RHTCLogo}
                  className="logo"
                  alt={t('Red Hat Training & Certification logo')}
                />
              </Link>
            </HeaderBase.Logo>
          )}
          <HeaderBase.Content>
            <HeaderBase.Navigation />
            <HeaderBase.SideMenu />
          </HeaderBase.Content>
        </HeaderBase>
        {canViewSubscriptionWidget && <SubscriptionWidget />}
        {showFeatureFlagManager && (
          <Suspense fallback={null}>
            <FeatureFlagManagerModal />
          </Suspense>
        )}
      </div>
    </FeatureFlagManagerProvider>
  );
};

export default observer(Header);
